"use client";

import React from "react";
import { MapPin, Phone, Mail, ArrowRight, Loader, Instagram, Facebook, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="236896" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          <Mail className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Application Received</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="practice_name" rules={{
        required: "Please enter your practice name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold font-default">Practice Name</FormLabel>
              <FormControl>
                <Input placeholder="Atlanta Medical Group" className="bg-background/50 border-border/50 focus-visible:border-primary h-12 text-base rounded-lg shadow-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">The official name of your medical facility.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="npi_number" rules={{
        required: "NPI Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold font-default">NPI Number</FormLabel>
              <FormControl>
                <Input placeholder="1234567890" className="bg-background/50 border-border/50 focus-visible:border-primary h-12 text-base font-mono rounded-lg shadow-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Your 10-digit National Provider Identifier.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="contact_role" rules={{
        required: "Please specify your role"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold font-default">Contact Role</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Lead Physician, Office Manager" className="bg-background/50 border-border/50 focus-visible:border-primary h-12 text-base rounded-lg shadow-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Your position within the practice.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Professional email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold font-default">Professional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="dr.smith@atlanta-med.com" className="bg-background/50 border-border/50 focus-visible:border-primary h-12 text-base rounded-lg shadow-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold font-default">Integration Notes <span className="text-muted-foreground/50 normal-case font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your current prescription workflow..." className="bg-background/50 border-border/50 focus-visible:border-primary min-h-[150px] text-base resize-y rounded-lg shadow-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold tracking-tight uppercase mt-4 group bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-lg transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Join the Network
            <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  return <section id="join-the-network" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-lg" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[30%] h-[30%] bg-secondary/10 blur-[100px] rounded-lg" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-start">
          
          <div className="lg:col-span-5 flex flex-col justify-between h-full space-y-12">
            <div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-default font-bold tracking-tighter mb-6 leading-[1.1]">
                Partner <br className="hidden md:block" />
                <span className="text-secondary">With GOTMEDS.IO.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-default">
                Join Atlanta's elite medical network. Streamline your patient care with secure, doctor-validated prescription delivery that sets a new standard in healthcare logistics.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-6 group">
                <div className="size-12 rounded-lg bg-muted flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300 shadow-sm">
                  <Phone className="size-5 text-foreground group-hover:text-primary-foreground transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold font-default mb-1">Provider Support</p>
                  <Link to="tel:+16783580270" className="text-2xl md:text-3xl font-default font-bold hover:text-secondary transition-colors tracking-tight">(678) 358-0270</Link>
                </div>
              </div>

              <div className="flex items-start space-x-6 group">
                <div className="size-12 rounded-lg bg-muted flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300 shadow-sm">
                  <Mail className="size-5 text-foreground group-hover:text-primary-foreground transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold font-default mb-1">Partnership Inquiries</p>
                  <Link to="mailto:info@gotmeds.io" className="text-xl md:text-2xl font-default font-bold hover:text-secondary transition-colors break-all tracking-tight">
                    info@gotmeds.io
                  </Link>
                </div>
              </div>

              <div className="flex items-start space-x-6 group">
                <div className="size-12 rounded-lg bg-muted flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300 shadow-sm">
                  <MapPin className="size-5 text-foreground group-hover:text-primary-foreground transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold font-default mb-1">Atlanta Hub</p>
                  <p className="text-xl md:text-2xl font-default font-bold tracking-tight">
                    Buckhead, Atlanta, GA
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border/50">
              <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold font-default mb-6">Clinical Excellence</p>
              <div className="flex space-x-4">
                <Link to="#" className="size-12 rounded-lg bg-muted flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 shadow-sm">
                  <Instagram className="size-5" />
                  <span className="sr-only">Instagram</span>
                </Link>
                <Link to="#" className="size-12 rounded-lg bg-muted flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 shadow-sm">
                  <Facebook className="size-5" />
                  <span className="sr-only">Facebook</span>
                </Link>
                <Link to="#" className="size-12 rounded-lg bg-muted flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 shadow-sm">
                  <Linkedin className="size-5" />
                  <span className="sr-only">LinkedIn</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-card rounded-2xl p-8 md:p-12 shadow-4xl border border-border/10 relative overflow-hidden backdrop-blur-sm">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-secondary to-transparent opacity-50" />
              
              <div className="mb-10">
                <h3 className="text-3xl font-default font-bold mb-2 tracking-tight">Provider Registration</h3>
                <p className="text-muted-foreground font-default">Submit your practice details to begin the integration process with our concierge team.</p>
              </div>

              <FormProvider formKey="join_network_form_medical_partners" formId="join_network_form_medical_partners" sectionName="Join the Network" defaultValues={{
              practice_name: "",
              npi_number: "",
              contact_role: "",
              email: "",
              message: ""
            }} successMessage="Thank you for your interest. Our medical integration team will review your NPI and contact you within 24 hours.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}